import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { ZODIAC_ORDER, ZODIAC_DATA } from '../constants';
import { ZodiacSign } from '../types';
import { ChevronLeft, ChevronRight, Play, Pause, Star, Globe2, Flame, Droplets, Wind, Mountain, Moon, ArrowUpCircle, Sun } from 'lucide-react';

interface ZodiacWheelProps {
  activeSign?: ZodiacSign | null;
  moonSign?: ZodiacSign | null;
  risingSign?: ZodiacSign | null;
  onSignClick?: (sign: ZodiacSign) => void;
  autoRotate?: boolean;
}

const ZodiacWheel: React.FC<ZodiacWheelProps> = ({ activeSign, moonSign, risingSign, onSignClick, autoRotate = true }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef<number>(0);
  
  // State
  const [rotation, setRotation] = useState(0);
  const [isAuto, setIsAuto] = useState(autoRotate);
  const [isDragging, setIsDragging] = useState(false);
  const [supports3D, setSupports3D] = useState(false);
  
  // Displayed sign state for the details panel (stable updates)
  const [displayedSign, setDisplayedSign] = useState<ZodiacSign | null>(null);

  // Constants
  const TOTAL_SIGNS = 12;
  const SECTOR_ANGLE = 360 / TOTAL_SIGNS;
  const AUTO_SPEED = 0.05; // Degrees per frame

  // ---------------------------------------------------------------------------
  // 1. Feature Detection (Progressive Enhancement)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Check for CSS 3D support safely
    if (typeof window !== 'undefined' && window.CSS && window.CSS.supports) {
      const has3D = window.CSS.supports('transform-style: preserve-3d');
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      setSupports3D(has3D && !prefersReducedMotion);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // 2. Core Rotation Loop
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const animate = () => {
      if (isAuto && !isDragging) {
        setRotation(prev => (prev - AUTO_SPEED) % 360);
      }
      requestRef.current = requestAnimationFrame(animate);
    };
    requestRef.current = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(requestRef.current);
  }, [isAuto, isDragging]);

  // ---------------------------------------------------------------------------
  // 3. Active Sign Sync (Prop -> Wheel)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (activeSign) {
      const index = ZODIAC_ORDER.indexOf(activeSign);
      if (index !== -1) {
        const target = -(index * SECTOR_ANGLE);
        setRotation(target);
        setIsAuto(false);
        setDisplayedSign(activeSign); // Immediate update for direct navigation
      }
    }
  }, [activeSign]);

  // ---------------------------------------------------------------------------
  // 4. Interaction Handlers
  // ---------------------------------------------------------------------------
  const handleManualMove = useCallback((direction: 'left' | 'right') => {
    const step = direction === 'left' ? SECTOR_ANGLE : -SECTOR_ANGLE;
    setIsAuto(false);
    setRotation(prev => {
      const snapped = Math.round(prev / SECTOR_ANGLE) * SECTOR_ANGLE;
      return snapped + step;
    });
  }, []);

  const toggleAuto = useCallback(() => {
    setIsAuto(prev => !prev);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') handleManualMove('left');
    if (e.key === 'ArrowRight') handleManualMove('right');
    if (e.key === ' ' || e.key === 'Enter') toggleAuto();
  };

  // Touch Handling for simple swipe
  const touchStartX = useRef<number | null>(null);
  
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    setIsDragging(true);
    setIsAuto(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.touches[0].clientX;
    // Sensitivity factor
    setRotation(prev => prev - (diff * 0.5));
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    touchStartX.current = null;
    // Snap to nearest
    setRotation(prev => Math.round(prev / SECTOR_ANGLE) * SECTOR_ANGLE);
  };

  // ---------------------------------------------------------------------------
  // 5. Render Helpers
  // ---------------------------------------------------------------------------
  
  // Calculate which sign is currently at the top (Angle ~ 0)
  const normalizedRotation = ((rotation % 360) + 360) % 360; 
  // Map rotation to index. 
  const topIndex = Math.round((360 - normalizedRotation) / SECTOR_ANGLE) % TOTAL_SIGNS;
  const currentTopSign = ZODIAC_ORDER[topIndex];

  // Update Displayed Sign only when wheel is NOT spinning freely (or dragging)
  // This prevents UI flicker during rapid rotation
  useEffect(() => {
    if (!isAuto && !isDragging) {
      setDisplayedSign(currentTopSign);
    } else if (displayedSign === null) {
      setDisplayedSign(currentTopSign);
    }
  }, [isAuto, isDragging, currentTopSign, displayedSign]);

  const details = displayedSign ? ZODIAC_DATA[displayedSign] : ZODIAC_DATA[ZodiacSign.Aries];

  const getElementIcon = (elem: string) => {
      switch(elem) {
          case 'Fire': return <Flame size={16} className="text-orange-400" />;
          case 'Water': return <Droplets size={16} className="text-blue-400" />;
          case 'Air': return <Wind size={16} className="text-cyan-200" />;
          case 'Earth': return <Mountain size={16} className="text-green-400" />;
          default: return <Star size={16} />;
      }
  };

  // Placement checks for the details panel
  const isSunSign = displayedSign === activeSign;
  const isMoonSign = displayedSign === moonSign;
  const isRisingSign = displayedSign === risingSign;

  return (
    <div className="flex flex-col items-center w-full max-w-[450px] mx-auto z-10 gap-8">
        
        {/* WHEEL CONTAINER */}
        <div 
          ref={containerRef}
          className="relative w-full aspect-square flex items-center justify-center shrink-0 focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-full"
          tabIndex={0}
          role="region"
          aria-label="Interactive Zodiac Wheel. Use Left/Right arrows to rotate."
          onKeyDown={handleKeyDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
            {/* --- CONTROLS --- */}
            <button
                onClick={() => handleManualMove('left')}
                className="absolute left-0 md:-left-12 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-cosmic-800/80 border border-primary/30 text-primary hover:bg-primary hover:text-black transition-all hover:scale-110 active:scale-95 shadow-[0_0_15px_rgba(255,200,44,0.2)]"
                aria-label="Rotate Counter-Clockwise"
            >
                <ChevronLeft size={32} />
            </button>

            <button
                onClick={() => handleManualMove('right')}
                className="absolute right-0 md:-right-12 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-cosmic-800/80 border border-primary/30 text-primary hover:bg-primary hover:text-black transition-all hover:scale-110 active:scale-95 shadow-[0_0_15px_rgba(255,200,44,0.2)]"
                aria-label="Rotate Clockwise"
            >
                <ChevronRight size={32} />
            </button>

            {/* --- WHEEL RENDERER --- */}
            {/* 3D Perspective Wrapper - Only applies if supported */}
            <div 
                className="w-full h-full relative"
                style={supports3D ? { 
                    transform: 'perspective(1000px) rotateX(25deg)',
                    transformStyle: 'preserve-3d',
                    transition: 'transform 0.5s ease'
                } : {}}
            >
                <svg 
                    viewBox="-50 -50 100 100" 
                    className="w-full h-full drop-shadow-[0_0_30px_rgba(168,85,247,0.3)] overflow-visible"
                    style={{ 
                        touchAction: 'none',
                        // If 3D is active, we might want to scale the SVG slightly to fit the tilt
                        transform: supports3D ? 'scaleY(1.1)' : 'none'
                    }}
                >
                    <defs>
                        <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                            <feGaussianBlur stdDeviation="2" result="blur" />
                            <feComposite in="SourceGraphic" in2="blur" operator="over" />
                        </filter>
                        {/* Stronger glow for active state */}
                        <filter id="glow-strong" x="-30%" y="-30%" width="160%" height="160%">
                            <feGaussianBlur stdDeviation="3.5" result="blur" />
                            <feComposite in="SourceGraphic" in2="blur" operator="over" />
                        </filter>
                        
                        <radialGradient id="wheelGradient" cx="50%" cy="50%" r="50%">
                            <stop offset="70%" stopColor="#0B1229" stopOpacity="0.9" />
                            <stop offset="100%" stopColor="#1E2952" stopOpacity="0.95" />
                        </radialGradient>
                    </defs>

                    {/* CSS Animations for SVG elements */}
                    <style>
                      {`
                        .zodiac-active {
                          animation: z-active 3s infinite ease-in-out;
                          transform-box: fill-box;
                          transform-origin: center;
                        }
                        .zodiac-inactive {
                          animation: z-inactive 5s infinite ease-in-out;
                          transform-box: fill-box;
                          transform-origin: center;
                        }
                        @keyframes z-active {
                          0%, 100% { transform: scale(1); filter: url(#glow); }
                          50% { transform: scale(1.15); filter: url(#glow-strong); }
                        }
                        @keyframes z-inactive {
                          0%, 100% { opacity: 0.6; transform: scale(0.95); }
                          50% { opacity: 1; transform: scale(1); }
                        }
                      `}
                    </style>

                    {/* Disc Background */}
                    <circle cx="0" cy="0" r="49" fill="url(#wheelGradient)" stroke="#A855F7" strokeWidth="0.5" opacity="0.8" />
                    <circle cx="0" cy="0" r="46" fill="none" stroke="#A855F7" strokeWidth="0.2" strokeDasharray="1 2" opacity="0.5" />

                    {/* Rotating Group. 0 deg = 3 o'clock default SVG. Rotate -90 to make 0 = Top. */}
                    <g transform={`rotate(-90)`}>
                        <g transform={`rotate(${rotation})`} style={{ transition: isDragging ? 'none' : 'transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)' }}>
                            {ZODIAC_ORDER.map((sign, i) => {
                                const angle = i * SECTOR_ANGLE;
                                const data = ZODIAC_DATA[sign];
                                const isActive = topIndex === i;
                                const isUserSun = sign === activeSign;
                                const isUserMoon = sign === moonSign;
                                const isUserRising = sign === risingSign;
                                
                                return (
                                    <g key={sign} transform={`rotate(${angle})`}>
                                        {/* Sector Lines */}
                                        <line x1="20" y1="0" x2="48" y2="0" stroke="white" strokeWidth="0.1" opacity="0.2" />
                                        
                                        {/* Sign Content - Counter Rotated to stay upright */}
                                        <g transform={`translate(36, 0)`}>
                                            <g transform={`rotate(${-(angle + rotation) + 90})`}> 
                                                
                                                {/* Highlight Active - Pulsing Background */}
                                                <circle 
                                                    r={isActive ? 8 : 0} 
                                                    fill="#FFC82C" 
                                                    opacity={isActive ? 0.15 : 0}
                                                    className={`transition-all duration-300 ${isActive ? 'animate-pulse' : ''}`}
                                                />

                                                {/* Symbol */}
                                                <text 
                                                    y={-2}
                                                    textAnchor="middle" 
                                                    dominantBaseline="middle" 
                                                    fill={isActive ? '#FFC82C' : '#A855F7'}
                                                    fontSize={isActive ? 10 : 7}
                                                    fontWeight="bold"
                                                    className={`transition-all duration-300 ${isActive ? 'zodiac-active' : 'zodiac-inactive'}`}
                                                    // Note: filter is handled by CSS animation class for active state
                                                    style={isActive ? {} : { filter: 'none' }} 
                                                >
                                                    {data.symbol}
                                                </text>

                                                {/* Label */}
                                                <text 
                                                    y={6}
                                                    textAnchor="middle" 
                                                    dominantBaseline="middle" 
                                                    fill={isActive ? '#FFFFFF' : '#94A3B8'}
                                                    fontSize={isActive ? 3.5 : 2.5}
                                                    fontWeight={isActive ? "bold" : "normal"}
                                                    letterSpacing="0.2"
                                                    className="transition-all duration-300 uppercase font-sans"
                                                >
                                                    {sign}
                                                </text>

                                                {/* Moon Indicator */}
                                                {isUserMoon && (
                                                    <g transform="translate(6, -6)">
                                                        <text fontSize="4" fill="#E0E7FF" filter="url(#glow)">🌙</text>
                                                    </g>
                                                )}

                                                {/* Rising Indicator */}
                                                {isUserRising && (
                                                    <g transform="translate(-6, -6)">
                                                        <text fontSize="2.5" fontWeight="bold" fill="#A855F7">ASC</text>
                                                    </g>
                                                )}

                                                {/* Hit Target */}
                                                <circle 
                                                    r="12" 
                                                    fill="transparent" 
                                                    className="cursor-pointer"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (onSignClick) onSignClick(sign);
                                                        setIsAuto(false);
                                                        setRotation(-(i * SECTOR_ANGLE));
                                                    }}
                                                    role="button"
                                                    aria-label={`Select ${sign}`}
                                                />
                                            </g>
                                        </g>
                                    </g>
                                );
                            })}
                        </g>
                    </g>

                    {/* Center Hub */}
                    <circle cx="0" cy="0" r="18" fill="#020410" stroke="#FFC82C" strokeWidth="0.5" className="shadow-lg" />
                    
                    {/* Play/Pause Toggle */}
                    <g 
                        className="cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); toggleAuto(); }}
                        role="button"
                        aria-label={isAuto ? "Pause Rotation" : "Start Rotation"}
                    >
                        <circle cx="0" cy="0" r="14" fill="transparent" /> 
                        {isAuto ? (
                            <g transform="translate(-3, -3) scale(0.25)">
                                <Pause color="white" fill="white" size={24} />
                            </g>
                        ) : (
                            <g transform="translate(-2.5, -3) scale(0.25)">
                                <Play color="#FFC82C" fill="#FFC82C" size={24} />
                            </g>
                        )}
                        <text y="8" fontSize="2" fill="gray" textAnchor="middle" letterSpacing="0.5">
                            {isAuto ? 'SPINNING' : 'PAUSED'}
                        </text>
                    </g>

                    {/* Top Indicator */}
                    <path d="M -3 -49 L 3 -49 L 0 -44 Z" fill="#FFC82C" filter="url(#glow)" />
                </svg>
            </div>
        </div>

        {/* DETAILS PANEL */}
        <div className="w-full relative min-h-[160px]" aria-live="polite">
            <div key={displayedSign || 'default'} className="bg-[#0B1229]/80 backdrop-blur-md border border-cosmic-700 rounded-2xl p-6 shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-500">
                <div className="flex flex-col md:flex-row gap-6 items-center md:items-start text-center md:text-left">
                    
                    <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-cosmic-800 to-black border border-cosmic-700 flex items-center justify-center text-5xl shadow-[0_0_20px_rgba(168,85,247,0.2)]">
                        {details.symbol}
                    </div>

                    <div className="flex-1 space-y-3">
                        <div className="flex flex-col md:flex-row md:items-center gap-2 justify-center md:justify-start">
                            <h3 className="text-3xl font-display font-bold text-white">
                                {displayedSign}
                            </h3>
                            <span className="text-primary font-mono text-sm uppercase tracking-widest border border-primary/20 bg-primary/10 px-2 py-0.5 rounded">
                                {details.dateRange}
                            </span>
                        </div>

                        {/* Personal Badges */}
                        {(isSunSign || isMoonSign || isRisingSign) && (
                            <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                                {isSunSign && (
                                    <span className="flex items-center gap-1 bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded text-xs border border-yellow-500/30">
                                        <Sun size={10} /> Sun Sign
                                    </span>
                                )}
                                {isMoonSign && (
                                    <span className="flex items-center gap-1 bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded text-xs border border-blue-500/30">
                                        <Moon size={10} /> Moon Sign
                                    </span>
                                )}
                                {isRisingSign && (
                                    <span className="flex items-center gap-1 bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded text-xs border border-purple-500/30">
                                        <ArrowUpCircle size={10} /> Rising Sign
                                    </span>
                                )}
                            </div>
                        )}

                        <div className="flex flex-wrap gap-3 justify-center md:justify-start text-sm font-medium">
                             <div className="flex items-center gap-1.5 text-gray-300 bg-white/5 px-3 py-1 rounded-full border border-white/10">
                                {getElementIcon(details.element)}
                                <span>{details.element}</span>
                             </div>
                             <div className="flex items-center gap-1.5 text-gray-300 bg-white/5 px-3 py-1 rounded-full border border-white/10">
                                <Globe2 size={16} className="text-purple-400" />
                                <span>Ruler: {details.rulingPlanet}</span>
                             </div>
                        </div>

                        <p className="text-gray-400 text-sm leading-relaxed max-w-xl">
                            {details.description}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
  );
};

export default ZodiacWheel;